import { stringify } from 'query-string';
import {
	fetchUtils,
	GET_LIST,
	GET_ONE,
	CREATE,
	UPDATE,
	DELETE,
	DELETE_MANY,
	GET_MANY,
	GET_MANY_REFERENCE,
	HttpError,
} from 'react-admin';
import { read_cookie } from 'sfcookies'
import { COOKIE_KEY, AUTH_HEADER, API_BASE_PATH_ADMIN } from '../constants';

const toSortField = (fieldName, direction) => {
	if (!fieldName) {
		return undefined;
	}
	let result =
		(direction === 'DESC' ? '-' : '+') + fieldName.charAt(0).toUpperCase() + fieldName.slice(1);
	return result;
};

const toPagingAndSorting = params => {
	const { field, order } = params.sort || {};
	const { page, perPage } = params.pagination || { page: 1, perPage: 50 };
	return {
		startWith: (page - 1) * perPage,
		count: perPage,
		sorting: toSortField(field, order)
	};
};

const fetchJson = (url, options = {}) => {
	return fetchUtils.fetchJson(url, options)
		.then((result) => {
			// Even in case of successful requests, the response may contain the "x-approval-required" header,
			// indicating that the request was accepted, but not yet executed.
			// This may lead to resources not being created, and React Admin misinterpreting the response payload ID
			// as the ID of the newly created entity. Therefore, we treat this case as an error.
			const _approvableAdminActionId = result.headers.get('x-approval-required');
			if (_approvableAdminActionId) {
				return Promise.reject(new HttpError('coreWalletAdmin.common.adminApprovalRequired', result.status, {
					_approvableAdminActionId,
				}));
			}
			return result;
		});
}

class DefaultMapper {
	run({ type, resource, params }) {
		const options = DefaultMapper.createOptions();
		const mappedResource = this.mapResource({ type, resource, params });
		switch (type) {
			case GET_MANY_REFERENCE:
			case GET_LIST:
				this.prepareGetList({
					type,
					resource,
					params,
					options,
					mappedResource
				});
				break;
			case GET_ONE:
				this.prepareGetOne({
					type,
					resource,
					params,
					options,
					mappedResource
				});
				break;
			case GET_MANY:
				this.prepareGetMany({
					type,
					resource,
					params,
					options,
					mappedResource
				});
				break;
			case UPDATE:
				this.prepareUpdate({
					type,
					resource,
					params,
					options,
					mappedResource
				});
				break;
			case CREATE:
				this.prepareCreate({
					type,
					resource,
					params,
					options,
					mappedResource
				});
				break;
			case DELETE_MANY:
				this.prepareDeleteMany({
					type,
					resource,
					params,
					options,
					mappedResource
				});
				break;
			case DELETE:
				this.prepareDeleteOne({
					type,
					resource,
					params,
					options,
					mappedResource
				});
				break;
			default:
				throw new Error(`Unsupported request type ${type}!`);
		}
		return this.doFetch({
			type,
			resource,
			params,
			options,
			mappedResource
		})
			.then(response => {
				const result =
					type === GET_MANY || type === DELETE_MANY
						? response.map(item => item.json)
						: response.json;
				switch (type) {
					case GET_MANY_REFERENCE:
					case GET_LIST:
						return this.finishGetList({
							type,
							resource,
							params,
							options,
							mappedResource,
							result
						});
					case GET_ONE:
						return this.finishGetOne({
							type,
							resource,
							params,
							options,
							mappedResource,
							result
						});
					case GET_MANY:
						return this.finishGetMany({
							type,
							resource,
							params,
							options,
							mappedResource,
							result
						});
					case UPDATE:
						return this.finishUpdate({
							type,
							resource,
							params,
							options,
							mappedResource,
							result
						});
					case CREATE:
						return this.finishCreate({
							type,
							resource,
							params,
							options,
							mappedResource,
							result
						});
					case DELETE_MANY:
						return this.finishDeleteMany({
							type,
							resource,
							params,
							options,
							mappedResource,
							result
						});
					case DELETE:
						return this.finishDeleteOne({
							type,
							resource,
							params,
							options,
							mappedResource,
							result
						});
					default:
						throw new Error(`Unsupported request type ${type}!`);
				}
			})
			.catch(error => {
				this.enhanceAndThrowRestError(error);
			});
	}

	/**
	 * Inspects the thrown error. In case of a backend error, it uses the response status code and error message.
	 *
	 * @throws The original error enhanced by a more detailed error message.
	 * @param {*} error The original error object from the api-response
	 */
	enhanceAndThrowRestError(error) {
		if (!error.body || !error.body._approvableAdminActionId) {
			let errorString = error.status + " - " + error.message;
			if (error.body && error.body.error && error.body.error.message) {
				errorString += ': ' + error.body.error.message;
			}
			error.message = errorString;
		}
		throw error;
	}

	static createOptions() {
		const options = {
			headers: new Headers({
				Accept: 'application/json'
			})
		};
		let authentication = read_cookie(COOKIE_KEY);
		if (authentication.length > 0) {
			authentication = JSON.parse(authentication);
			options.headers.set(AUTH_HEADER, `${authentication.apiKey}`);
		}
		return options;
	}

	mapResource({ resource }) {
		return resource;
	}

	mapResult({ type, resource, params, options, mappedResource, result }) {
		return result.data;
	}

	prepareGetList({ type, resource, params, options, mappedResource }) {
		const query = {
			...params.filter,
			...toPagingAndSorting(params)
		};
		options.url = `${API_BASE_PATH_ADMIN}/${mappedResource}?${stringify(query)}`;
	}

	finishGetList({ type, resource, params, options, mappedResource, result }) {
		let data = this.mapResult({ result });
		return {
			data: data,
			total: result.meta.totalItems
		};
	}

	finishCreate({ type, resource, params, options, mappedResource, result }) {
		let data = this.mapResult({ type, resource, params, options, mappedResource, result });
		return { data };
	}

	prepareGetOne({ type, resource, params, options, mappedResource }) {
		options.url = `${API_BASE_PATH_ADMIN}/${mappedResource}/${params.id}`;
	}
	finishGetOne({ type, resource, params, options, mappedResource, result }) {
		let data = this.mapResult({ result });
		return { data };
	}

	prepareGetMany({ type, resource, params, options, mappedResource }) {
		options.url = `${API_BASE_PATH_ADMIN}/${mappedResource}/{id}`;
		options._ids = params.ids;
	}
	finishGetMany({ type, resource, params, options, mappedResource, result }) {
		return { data: result.map(item => item.data) };
	}

	prepareUpdate({ type, resource, params, options, mappedResource }) {
		options.url = `${API_BASE_PATH_ADMIN}/${mappedResource}/${params.id}`;
		options.method = 'PUT';
		options.body = JSON.stringify(params.data);
	}

	prepareCreate({ type, resource, params, options, mappedResource }) {
		options.url = `${API_BASE_PATH_ADMIN}/${mappedResource}`;
		options.method = 'POST';
		options.body = JSON.stringify(params.data);
	}

	finishUpdate({ type, resource, params, options, mappedResource, result }) {
		let data = this.mapResult({ type, resource, params, options, mappedResource, result });
		return { data };
	}

	prepareDeleteMany({ type, resource, params, options, mappedResource }) {
		options.url = `${API_BASE_PATH_ADMIN}/${mappedResource}/{id}`;
		options.method = 'DELETE';
		options._ids = params.ids;
	}

	prepareDeleteOne({ type, resource, params, options, mappedResource }) {
		options.url = `${API_BASE_PATH_ADMIN}/${mappedResource}/{id}`;
		options.method = 'DELETE';
		options._ids = params.ids;
	}

	finishDeleteOne({ type, resource, params, options, mappedResource, result }) {
		let data = this.mapResult({ type, resource, params, options, mappedResource, result });
		return { data };
	}

	finishDeleteMany({ type, resource, params, options, mappedResource, result }) {
		return { data: params.ids };
	}

	doFetch({ type, resource, params, options, mappedResource }) {
		if (type === DELETE_MANY || type === GET_MANY) {
			return Promise.all(
				options._ids.map(id =>
					fetchJson(options.url.replace(/{id}/g, id), options)
				)
			);
		} else {
			return fetchJson(options.url, options);
		}
	}
}

function mapIdInList(result, idFieldName) {
	return {
		...result,
		data: result.data.map(item => {
			return {
				...item,
				id: item[idFieldName]
			};
		})
	};
}

function mapId(result, idFieldName) {
	return {
		...result,
		data: {
			...result.data,
			id: result.data[idFieldName]
		}
	};
}

/**
 * An custom method defines a Custom Rest Action such as ':lock'
 * https://example.com/wallets/123:lock
 */
function addCustomMethods(params) {
	return params && params['method'] ? ':' + params['method'] : '';
}

export { fetchJson, mapIdInList, mapId, addCustomMethods, toSortField, toPagingAndSorting };
export default DefaultMapper;
